import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import React from 'react';

import { COLORS } from '@constants/colors';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  href?: string;
  target?: string;
  rel?: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'sage'
    | 'gold'
    | 'outline'
    | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'rounded' | 'pill' | 'square';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  className?: string;
  style?: React.CSSProperties;
  animate?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  href,
  target,
  rel,
  variant = 'primary',
  size = 'md',
  shape = 'rounded',
  fullWidth = false,
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'right',
  iconSize = 16,
  className = '',
  style = {},
  animate = true,
  type = 'button',
}) => {
  // Variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          background: `linear-gradient(135deg, ${COLORS.gold} 0%, #F97316 100%)`,
          color: COLORS.primary,
          border: 'none',
          hoverShadow: '0 15px 30px rgba(245, 158, 11, 0.3)',
        };
      case 'secondary':
        return {
          background: `linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.primary} 100%)`,
          color: COLORS.pearl,
          border: 'none',
          hoverShadow: '0 15px 30px rgba(30, 58, 95, 0.3)',
        };
      case 'accent':
        return {
          backgroundColor: COLORS.accent,
          color: 'white',
          border: 'none',
          hoverShadow: '0 10px 25px rgba(59, 130, 246, 0.3)',
        };
      case 'sage':
        return {
          backgroundColor: COLORS.sage,
          color: 'white',
          border: 'none',
          hoverShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
        };
      case 'gold':
        return {
          backgroundColor: COLORS.gold,
          color: COLORS.primary,
          border: 'none',
          hoverShadow: '0 10px 25px rgba(245, 158, 11, 0.3)',
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: COLORS.accent,
          border: `2px solid ${COLORS.accent}`,
          hoverShadow: '0 5px 15px rgba(59, 130, 246, 0.2)',
        };
      case 'ghost':
        return {
          backgroundColor: 'white',
          color: COLORS.charcoal,
          border: '1px solid #e2e8f0',
          hoverShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
        };
      default:
        return {
          backgroundColor: COLORS.accent,
          color: 'white',
          border: 'none',
          hoverShadow: '0 10px 25px rgba(59, 130, 246, 0.3)',
        };
    }
  };

  // Size styles
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          minHeight: '2rem',
        };
      case 'md':
        return {
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          minHeight: '2.5rem',
        };
      case 'lg':
        return {
          padding: '1rem 2rem',
          fontSize: '1.125rem',
          minHeight: '3rem',
        };
      case 'xl':
        return {
          padding: '1.25rem 2.5rem',
          fontSize: '1.25rem',
          minHeight: '3.5rem',
        };
      default:
        return {
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          minHeight: '2.5rem',
        };
    }
  };

  // Shape styles
  const getShapeStyles = () => {
    switch (shape) {
      case 'rounded':
        return { borderRadius: '0.75rem' }; // rounded-xl
      case 'pill':
        return { borderRadius: '9999px' }; // rounded-full
      case 'square':
        return { borderRadius: '0.375rem' }; // rounded-md
      default:
        return { borderRadius: '0.75rem' };
    }
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();
  const shapeStyles = getShapeStyles();

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    textDecoration: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    opacity: disabled ? 0.6 : 1,
    width: fullWidth ? '100%' : 'auto',
    ...variantStyles,
    ...sizeStyles,
    ...shapeStyles,
    ...style,
  };

  const content = (
    <>
      {Icon && iconPosition === 'left' && (
        <Icon size={iconSize} className="mr-2" />
      )}
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
      ) : null}
      <span className="relative z-10">{children}</span>
      {Icon && iconPosition === 'right' && (
        <Icon
          size={iconSize}
          className={`ml-2 ${animate ? 'group-hover:translate-x-1 transition-transform' : ''}`}
        />
      )}
    </>
  );

  const motionProps = animate
    ? {
        whileHover: disabled
          ? {}
          : {
              scale: 1.03,
              y: -1,
              boxShadow: variantStyles.hoverShadow,
            },
        whileTap: disabled ? {} : { scale: 0.97 },
      }
    : {};

  const commonProps = {
    className: `group ${className}`,
    style: baseStyles,
    onClick: disabled ? undefined : onClick,
    disabled,
    ...motionProps,
  };

  if (href && !disabled) {
    return (
      <motion.a href={href} target={target} rel={rel} {...commonProps}>
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button type={type} {...commonProps}>
      {content}
    </motion.button>
  );
};

export default Button;
